import React from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { makeStyles, createStyles, useMediaQuery } from '@material-ui/core';

import {
    Typography,
    Variant,
    Police,
    Button,
    ButtonVariant,
    ContainerMui,
    Spacer
} from '../../styles';
import { IResumeOffre } from '../../hooks';

import { ResumeMontant } from '../../components';

const useStyles = makeStyles(() =>
    createStyles({
        note: {
            fontSize: '0.875rem',
            fontWeight: 'normal'
        },
        titre: {
            fontSize: '24px !important'
        }
    })
);

export interface IResumeModalitesProps {
    isEditButtonDisplayed: boolean;
    isFrequenceModifiee: boolean;
    resumeOffre: IResumeOffre;
    onModifiedClick: () => void;
}

export const ResumeModalites: React.FC<IResumeModalitesProps> = ({
    isEditButtonDisplayed,
    isFrequenceModifiee,
    resumeOffre,
    onModifiedClick
}) => {
    const [t] = useTranslation(['ResumeModalites']);
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:640px)');

    const amortissement = resumeOffre?.offreChoisie.amortissement;

    const formattedAnnees = t('Date:years', {
        value: amortissement?.annee
    });

    const formattedMois = t('Date:months', {
        value: amortissement?.mois
    });

    return (
        <ContainerMui
            dataTestId="bloc_modalite_validation"
            padding={isMobile ? '1rem' : '1.5rem'}
            spacerDistance={6}
            spacerDistanceMobile={5}
        >
            <Typography
                variant={Variant.h3}
                police={Police.bold}
                marginBottom="0.75rem"
                className={classes.titre}
            >
                {t('resumeModalitesTitle')}
            </Typography>
            {isFrequenceModifiee ? (
                <>
                    <Typography variant={Variant.p} marginBottom="0.25rem">
                        {t('resumeModalitesTypeTitle')}
                    </Typography>
                    <Typography
                        variant={Variant.p}
                        police={Police.bold}
                        marginBottom="1rem"
                    >
                        {t(resumeOffre?.offreChoisie.frequenceRemboursement)}
                    </Typography>
                </>
            ) : (
                <>
                    <Typography variant={Variant.p} marginBottom="0.25rem">
                        {t('resumeModalitesProchainePaiementTitle')}
                    </Typography>
                    <Typography
                        variant={Variant.p}
                        police={Police.bold}
                        marginBottom="1rem"
                    >
                        <span
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    t('Date:fullCapitalizedFirstLetter', {
                                        value: resumeOffre?.dateProchainPaiement
                                    })
                                )
                            }}
                        />
                    </Typography>
                </>
            )}
            <Typography variant={Variant.p} marginBottom="0.25rem">
                {t('resumeModalitesAmortissementTitle')}
            </Typography>
            <Typography
                variant={Variant.p}
                police={Police.bold}
                marginBottom="1rem"
            >
                {t('resumeModalitesAmortissementPrefix')}
                {amortissement?.annee > 0 && formattedAnnees}
                {amortissement?.mois > 0 &&
                    t('resumeModalitesAmortissementJoin') + ' ' + formattedMois}
            </Typography>
            <ResumeMontant resumeOffre={resumeOffre} />
            {/* Ne pas afficher si MARTINE ( page validation ou choix 1er coemprunteur ) */}
            {isEditButtonDisplayed && (
                <>
                    <Spacer all={5} sm={6} />
                    <Button
                        id={'QA_test_validation_date_modifier'}
                        variant={ButtonVariant.compact}
                        dataCy="qa-button-date-modifier"
                        dataTestId={'bouton_modifier_modalites'}
                        onClick={onModifiedClick}
                        srOnlySuffix={
                            ' ' + t('resumeChangeButtonLabelHorsEcran')
                        }
                    >
                        {t('resumeChangeButton')}
                    </Button>
                </>
            )}
        </ContainerMui>
    );
};
